/**
 * InstrumentSearch — broker scrip-master search with a segment picker.
 *
 * Used by the order ticket (pick what to trade) and the strategy form (pick
 * instruments to watch). F&O segments expose expiry / option-type / strike
 * filters so an option leg can be found without paging through the chain.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Plus, Search } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { formatNumber } from '@/lib/format';
import { Alert, Badge, Button, Input, Select } from '@/components/ui';
import { SEGMENT_OPTIONS, isDerivativeSegment } from '@/features/market/instruments';

export interface InstrumentResult {
  instrument_token: string;
  trading_symbol: string;
  name: string;
  exchange_segment: string;
  series: string;
  lot_size: number;
  tick_size: string | number;
  isin?: string;
  instrument_type?: string;
  expiry?: string;
  strike_price?: string | number;
  option_type?: string;
}

interface InstrumentSearchProps {
  onSelect: (result: InstrumentResult) => void;
  /** Label for the per-row action button. */
  actionLabel?: string;
  defaultSegment?: string;
  /** Tokens already chosen — rendered as disabled so they aren't added twice. */
  selectedTokens?: string[];
  autoFocus?: boolean;
}

export function InstrumentSearch({
  onSelect,
  actionLabel = 'Select',
  defaultSegment = 'nse_cm',
  selectedTokens = [],
  autoFocus = true,
}: InstrumentSearchProps) {
  const [query, setQuery] = useState('');
  const [segment, setSegment] = useState(defaultSegment);
  const [optionType, setOptionType] = useState('');
  const [expiry, setExpiry] = useState('');
  const [strike, setStrike] = useState('');
  const [results, setResults] = useState<InstrumentResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const derivatives = isDerivativeSegment(segment);

  const runSearch = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setIsSearching(true);
    setError(null);
    try {
      const body: Record<string, string> = { query: query.trim(), exchange_segment: segment };
      if (derivatives) {
        if (optionType) body.option_type = optionType;
        if (expiry.trim()) body.expiry = expiry.trim().toUpperCase();
        if (strike.trim()) body.strike_price = strike.trim();
      }
      const { data } = await api.post('/brokers/search', body);
      setResults(data);
      setSearched(true);
    } catch (err) {
      setResults([]);
      setError(getErrorMessage(err));
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <form onSubmit={runSearch} className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Select
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            aria-label="Exchange segment"
            className="w-36"
          >
            {SEGMENT_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Input
            autoFocus={autoFocus}
            icon={Search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={derivatives ? 'NIFTY, BANKNIFTY, RELIANCE…' : 'RELIANCE, INFY, TCS…'}
            aria-label="Search instruments"
            className="flex-1"
          />
          <Button type="submit" variant="primary" loading={isSearching}>
            Search
          </Button>
        </div>

        {derivatives && (
          <div className="grid grid-cols-3 gap-2">
            <Select value={optionType} onChange={(e) => setOptionType(e.target.value)} aria-label="Option type">
              <option value="">Futures + options</option>
              <option value="CE">Calls (CE)</option>
              <option value="PE">Puts (PE)</option>
            </Select>
            <Input
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              placeholder="Expiry 25SEP2026"
              aria-label="Expiry"
              mono
            />
            <Input
              value={strike}
              onChange={(e) => setStrike(e.target.value)}
              placeholder="Strike 24800 or 24000-25000"
              aria-label="Strike price"
              mono
            />
          </div>
        )}
      </form>

      {error && <Alert tone="danger">{error}</Alert>}

      {results.length > 0 && (
        <ul className="max-h-72 divide-y divide-line overflow-y-auto rounded-lg border border-line">
          {results.map((result) => {
            const chosen = selectedTokens.includes(result.instrument_token);
            const strikeValue = Number(result.strike_price ?? 0);
            return (
              <li
                key={`${result.instrument_token}-${result.exchange_segment}`}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-fg">
                    <span className="truncate">{result.trading_symbol}</span>
                    {result.option_type && <Badge tone={result.option_type === 'CE' ? 'success' : 'danger'}>{result.option_type}</Badge>}
                    {result.instrument_type && !result.option_type && (
                      <Badge tone="neutral">{result.instrument_type}</Badge>
                    )}
                  </p>
                  <p className="truncate text-xs text-fg-subtle">{result.name}</p>
                  <p className="font-tabular text-xs text-fg-faint uppercase">
                    {result.exchange_segment?.replace('_', ' ')} · {result.instrument_token}
                    {result.lot_size > 1 && ` · lot ${result.lot_size}`}
                    {result.expiry && ` · ${result.expiry}`}
                    {strikeValue > 0 && ` · ${formatNumber(strikeValue)}`}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Plus}
                  disabled={chosen}
                  onClick={() => onSelect(result)}
                  aria-label={`${actionLabel} ${result.trading_symbol}`}
                >
                  {chosen ? 'Added' : actionLabel}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {!isSearching && !error && searched && results.length === 0 && (
        <p className="py-3 text-center text-xs text-fg-subtle">No instruments matched. Try the symbol name (e.g. RELIANCE).</p>
      )}
      {!isSearching && !error && !searched && (
        <p className="py-1 text-xs text-fg-faint">
          Searches your broker's scrip master. Needs an active broker session.
        </p>
      )}
    </div>
  );
}
